import { useState } from 'react';
import ServiceDetailHeader from '../../molecules/ServiceDetailHeader';
import TermsConditionServices from '../../atoms/TermsConditionServices';
import Divider from '../../atoms/Divider';
import ServiceTags from '../../molecules/ServiceTags';
import ModalServiceDetailOptions from '../ModalServiceDetailOptions';
import InfoSection from '../InfoSection';
import PriceAccessories from '../../molecules/PriceAccessories';
import VoucherCard from '../../atoms/VoucherCard';
import RightIcon from '../../atoms/Icons/RightIcon';
import PercentIcon from '../../atoms/Icons/PercentIcon';
import { ICouponDiscount } from '../../../interface/product.interfaces';
import { IAccessoriesDetail } from '../../../interface/accessories.interface';
import { getPickUpLabelAccessories } from '../../../helpers/utils';
import DiscIcon from '../../atoms/Icons/DiscIcon';
import eventViewAccessoriesPDP from '../../../lib/gtm/eventViewAccessoriesPDP';
import ServiceDetailBanner from '../../molecules/ServiceDetailBanner';

import { useIsElementVisible } from '../../../hooks';
import { IBreadcrumbs } from '../../molecules/Breadcrumbs';

interface AccessoriesDetailSectionProps {
  serviceType?: 'tintouch' | 'accessories';
  isCMS?: boolean;
  accessoriesDetail: IAccessoriesDetail;
  termAndCondition?: { title?; string };
  serviceProductList?: { title?; string }[];
  triggerGTM?: {
    status: boolean;
  };
  serviceDiscounts?: ICouponDiscount[];
  minimumDeliveryFee: {
    value: string;
  };
  quantity: number;
  setQuantity: (val: number) => void;
  emarsysData?: {
    pageTitle: string;
    breadcrumbItems: IBreadcrumbs[];
    sku: string;
    skuPrice: string;
    carModel?: string[];
  };
}

const AccessoriesDetailSection = ({
  termAndCondition,
  accessoriesDetail,
  serviceDiscounts,
  isCMS = false,
  minimumDeliveryFee,
  quantity,
  setQuantity,
  triggerGTM = { status: true },
  serviceType,
  emarsysData = { pageTitle: '', breadcrumbItems: [], sku: '', skuPrice: '', carModel: [] },
}: AccessoriesDetailSectionProps) => {
  const [showTermsModalTnc, setShowTermsModalTnc] = useState<boolean>(false);
  const [showVoucherModal, setShowVoucherModal] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);

  const infoDescription = {
    title: 'DESKRIPSI PRODUK',
    desc: accessoriesDetail?.productDescription,
  };

  const fromatMinimumDeliveryFee = minimumDeliveryFee.value
    ? new Intl.NumberFormat('id-ID').format(Number(minimumDeliveryFee.value))
    : '-';

  const infoDelivery = {
    title: 'ongkos kirim',
    desc: `Estimasi ongkos kirim mulai dari <strong> Rp${fromatMinimumDeliveryFee} </strong>`,
  };

  const imageDetail = {
    detail: accessoriesDetail?.accessoriesImages || [],
    imageUrl: accessoriesDetail?.accessoriesImages[0]?.imageUrl
      ? accessoriesDetail?.accessoriesImages[0]?.imageUrl
      : '',
    imageAlt: accessoriesDetail?.accessoriesImages[0]?.imageAlt
      ? accessoriesDetail?.accessoriesImages[0]?.imageAlt
      : '',
  };

  const nextPickUpOptions = accessoriesDetail?.pickUpOptions?.map((pickUpOption) => ({
    ...pickUpOption,
    code: pickUpOption.code
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase()),
  }));

  const pickUpLabels: { text: string; iconUrl: string; hide?: boolean }[] = nextPickUpOptions
    ? getPickUpLabelAccessories(nextPickUpOptions, true)
    : [];
  const ref = useIsElementVisible(() =>
    eventViewAccessoriesPDP(
      {
        name: accessoriesDetail?.contentTitle?.toLowerCase() || 'not available',
        id: accessoriesDetail?.accessoriesCode?.toLowerCase() || 'not available',
        price: Math.floor(
          accessoriesDetail?.strikeThroughPrice?.value || accessoriesDetail?.price?.value || 0
        ),
        brand: 'toyota',
        category: 'aksesoris',
        variant: `${accessoriesDetail?.accCategory?.toLowerCase() || 'not available'}`,
        discountedPrice: Math.floor(
          accessoriesDetail?.price?.value - accessoriesDetail?.strikeThroughPrice?.value || 0
        ),
      },
      accessoriesDetail?.accCategory?.toLowerCase()
    )
  );

  return (
    <>
      <div ref={ref} className="text-reliableBlack lg:pt-6 lg:px-5">
        <div className="grid grid-cols-1 lg:container xl:px-0 lg:grid-cols-[1.5fr_2fr] lg:gap-[52px]">
          <ServiceDetailBanner
            imageUrl={imageDetail}
            isCMS={isCMS}
            isSticky={false}
            typeID="accessories"
            containerClassName="container !px-0 sm:!px-4 lg:!pr-0 lg:!px-0"
          />

          <div className="container block mb-1 !px-0 sm:!px-4 lg:hidden">
            <ServiceTags items={pickUpLabels} className="" width={200} size="large" />
          </div>

          <div className="lg:sticky lg:top-[197px]">
            <ServiceDetailHeader
              serviceModelTag={pickUpLabels}
              title={accessoriesDetail?.contentTitle}
              desc={accessoriesDetail?.contentDescription}
              containerClassname="container !px-0 mb-3 lg:!mb-0"
              isCMS={isCMS}
              impressionData={{
                pageTitle: emarsysData.pageTitle,
                breadCrumb: emarsysData.breadcrumbItems,
                carModel: emarsysData.carModel,
                sku: emarsysData.sku,
                skuPrice: emarsysData.skuPrice,
              }}
              feature="ACCESSORIES"
            />

            <div className="container block lg:hidden">
              <PriceAccessories
                quantity={quantity}
                setQuantity={setQuantity}
                accessoriesDetail={accessoriesDetail}
                serviceType={serviceType}
                impressionData={emarsysData}
              />
            </div>
            {(!!serviceDiscounts.length || isCMS) && (
              <div className="container w-full py-4 pb-0 mb-2 lg:px-0 lg:pt-0 lg:mb-4">
                <button
                  className="block w-full"
                  onClick={isCMS ? () => {} : () => setShowVoucherModal(true)}
                >
                  <div className="flex overflow-x-hidden lg:px-0">
                    <div className="relative basis-12 grow-0 shrink-0 lg:basis-16">
                      <div className="w-full h-full">
                        <DiscIcon />
                      </div>
                      <p className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 ml-0.5">
                        <PercentIcon />
                      </p>
                    </div>

                    <VoucherCard className="w-full py-3 pl-3 pr-6 lg:pr-9" borderColor="#E9E9E9">
                      <div className="flex justify-between items-center self-stretch h-full">
                        <div className="text-start">
                          <p className="text-xs font-semibold text-reliableBlack90 mb-0.5 uppercase lg:text-sm lg:mb-1">
                            {accessoriesDetail?.voucherTitle}
                          </p>
                          <p className="text-[8px] text-reliableBlack60 line-clamp-1 md:text-xs">
                            {accessoriesDetail?.voucherDescription}
                          </p>
                        </div>
                        <div>
                          <RightIcon />
                        </div>
                      </div>
                    </VoucherCard>
                  </div>
                </button>
              </div>
            )}

            {infoDescription.desc && (
              <InfoSection
                info={infoDescription}
                sectionClassName={'container !mt-4 lg:!mt-0 !mb-4'}
                labelClassname={'text-xs font-medium text-hyperLink uppercase !mt-2.5'}
                headingClassName="text-sm !mb-1.5 lg:text-base"
                contentClassName="text-xs lg:text-sm"
                paddingClassname="0"
                containerClassName="!min-w-full lg:px-0"
                feature="ACCESSORIES_DESCRIPTION"
                impressionData={{
                  breadcrumbItems: emarsysData.breadcrumbItems,
                  pageTitle: emarsysData.pageTitle,
                  sku: emarsysData.sku,
                  skuPrice: emarsysData.skuPrice,
                  carModels: emarsysData.carModel,
                }}
                idName="product-description"
              />
            )}

            {minimumDeliveryFee?.value &&
              pickUpLabels.some((item) => item.text === 'Kirim ke Rumah') && (
                <InfoSection
                  paddingClassname="0"
                  info={infoDelivery}
                  sectionClassName={'container !mt-4 mb-0 lg:mb-4 lg:!mt-0'}
                  labelClassname={'text-xs font-medium text-hyperLink uppercase'}
                  headingClassName="text-sm !mb-1.5 lg:text-base"
                  contentClassName="text-xs lg:text-sm"
                  containerClassName="!min-w-full lg:px-0"
                  idName="terms-and-conditions"
                />
              )}

            <Divider
              additionalClassName="h-2 mt-5 mb-4"
              bgColor="bg-reliableBlack5"
              hideOnDesktop={true}
            />

            <TermsConditionServices
              setShowTermsModalTnc={setShowTermsModalTnc}
              isCMS={isCMS}
              termCondition={termAndCondition}
              showFullContent={false}
              headingClassName="break-all"
              containerClassName="container pt-0 pb-4 lg:pb-0 lg:!px-0"
              contentClassName="text-xs leading-[18px] lg:text-sm lg:leading-6"
              feature="ACCESSORIES"
              emarsysData={emarsysData}
            />
          </div>
        </div>

        <ModalServiceDetailOptions
          showTermsModal={showTermsModal}
          setShowTermsModal={setShowTermsModal}
          setShowVoucherModal={setShowVoucherModal}
          setShowTermsModalTnc={setShowTermsModalTnc}
          showVoucherModal={showVoucherModal}
          showTermsModalTnc={showTermsModalTnc}
          termCondition={termAndCondition}
          serviceDiscounts={serviceDiscounts}
          serviceType={serviceType}
          feature="ACCESSORIES"
          impressionData={emarsysData}
        />
      </div>
    </>
  );
};

export default AccessoriesDetailSection;
